import { Box, Chip, Container, Divider, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Footer from "../components/common/Footer";

const stack = [
  { layer: "Frontend", items: ["React 18", "TypeScript", "Vite 6"] },
  { layer: "UI & Styling", items: ["Material UI v6", "Emotion"] },
  { layer: "Routing", items: ["React Router v7"] },
  { layer: "Authentication", items: ["AWS Cognito", "AWS Amplify 6", "Google SSO"] },
  { layer: "Backend", items: ["AWS Lambda (Node.js 22)", "AWS API Gateway", "esbuild"] },
  { layer: "Database", items: ["AWS DynamoDB"] },
  { layer: "Email Providers", items: ["Brevo", "Resend"] },
  { layer: "Secrets", items: ["AWS SSM Parameter Store (SecureString)"] },
  { layer: "Infrastructure", items: ["AWS CloudFormation"] },
  { layer: "CI/CD", items: ["GitHub Actions (OIDC)"] },
];

export default function TechnologyPage() {
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ flex: 1, py: 6 }}>
        <Link component={RouterLink} to="/" variant="body2" color="text.secondary" underline="hover" sx={{ display: "inline-block", mb: 4 }}>
          ← Back to īmēra
        </Link>

        <Typography variant="h4" gutterBottom>Technology</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
          īmēra is a single transactional email hub that routes outbound mail through Brevo or Resend depending on the from-domain. It runs entirely on AWS serverless infrastructure — no servers to manage, scales automatically with demand.
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 6 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#FDFCFB" }}>
                <TableCell sx={{ fontWeight: 600, width: "35%" }}>Layer</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Technologies</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stack.map((row) => (
                <TableRow key={row.layer} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{row.layer}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {row.items.map((item) => (
                        <Chip key={item} label={item} size="small" sx={{ bgcolor: "#F0EDEA", color: "text.primary", fontSize: "0.75rem" }} />
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>Serverless Architecture</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Every API endpoint runs as an individual AWS Lambda function invoked via API Gateway. There are no long-running servers — functions spin up on demand and shut down immediately, keeping costs proportional to usage.
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>Provider Routing</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Each registered from-domain has a primary and optional fallback provider. If the primary provider rejects a request, īmēra automatically retries through the fallback. Sending from any unverified domain returns <code>400 unknown_from_domain</code>.
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>Audit & Webhooks</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Every send is recorded in DynamoDB with the originating app, recipient, status, latency, and provider message ID. Brevo and Resend webhook callbacks (delivered, bounced, opened, clicked) are correlated by message ID and surfaced in the Audit page.
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>Security</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Dashboard authentication is handled by AWS Cognito (email/password and Google SSO) with JWT-based session tokens and a pre-signup email allowlist. Consuming apps authenticate via per-app API keys; the plaintext is shown once and only the hash is stored. Provider secrets live in AWS SSM Parameter Store as SecureString. All traffic is encrypted in transit via HTTPS.
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>Open Source</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              īmēra is built entirely on open-source software. The TypeScript SDK <code>@imera/client</code> is also open source, so consuming apps can integrate without lock-in.
            </Typography>
          </Box>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
}
